import { useRef } from "react";
import { LDSHeading, LDSMenu } from "@resmed/lumina-design-system";
import Icons from "@resmed/lumina-design-system-assets/dist/icons";
import SvgAvatar from "../SvgAvatar";
import SvgIconButton from "../SvgIconButton";

interface MenuItem {
  id?: string;
  label?: string;
  icon?: string;
  separator?: boolean;
  severity?: "default" | "danger" | "success";
  command?: () => void;
}

/** Matches LDSMenuRef shape from @resmed/lumina-design-system */
interface MenuRef {
  toggle(event: React.SyntheticEvent): void;
  show(event: React.SyntheticEvent): void;
  hide(event: React.SyntheticEvent): void;
  getElement(): HTMLDivElement | null;
  getTarget(): EventTarget | null;
}

export interface ChatHeaderProps {
  name: string;
  /** Determines which SVG icon to show: single-user or group */
  icon?: "single" | "multiple";
  onClose?: () => void;
  onMenuItemSelect?: (id: string) => void;
  /** Hide the three-dot menu button (e.g. for archived/read-only conversations) */
  showMenu?: boolean;
  pinnedStatus?: boolean;
  participantsCount?: number;
}

const ChatHeader = ({
  name,
  icon = "single",
  onClose,
  onMenuItemSelect,
  showMenu = true,
  pinnedStatus = false,
  participantsCount = 0,
}: ChatHeaderProps) => {
  const menuRef = useRef<MenuRef>(null);

  const menuItems: MenuItem[] = [
    { id: "addParticipant", label: "Add Participants" },
    { id: "viewParticipants", label: "View Participants" },
    ...(participantsCount > 2
      ? [{ id: "renameGroup", label: "Rename Group" }]
      : []),
    {
      id: "pinConversation",
      label: pinnedStatus ? "Unpin Conversation" : "Pin Conversation",
    },
    { id: "leaveConversation", label: "Leave Conversation", severity: "danger" as const },
  ].map((item) => ({
    ...item,
    command: item.id ? () => onMenuItemSelect?.(item.id!) : undefined,
  }));

  return (
    <div className="lds-flex lds-items-center lds-gap-3 lds-px-4 lds-py-3 lds-border-b lds-border-bordercolor-default lds-bg-white lds-shrink-0">
      {/* Avatar — uses same SVG icons as conversation list */}
      <SvgAvatar icon={icon} size="medium" />

      {/* Name — 2-line clamp, no subtitle */}
      <div className="lds-flex-1 lds-min-w-0">
        <LDSHeading
          size="h5"
          className="lds-font-semibold lds-text-textcolor-default lds-leading-[125%] [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] lds-overflow-hidden"
        >
          {name}
        </LDSHeading>
      </div>

      {/* Actions: three-dot menu + close */}
      <div className="lds-flex lds-items-center lds-gap-3 lds-shrink-0">
        {showMenu && (
          <>
            <LDSMenu ref={menuRef} model={menuItems} popup />
            <SvgIconButton
              icon={Icons.FaEllipsis}
              ariaLabel="More options"
              size={24}
              data-testid="chat-header-menu-btn"
              onClick={(e) => menuRef.current?.toggle(e)}
            />
          </>
        )}
        <SvgIconButton
          icon={Icons.FaXmark}
          ariaLabel="Close"
          size={24}
          data-testid="chat-header-close-btn"
          onClick={onClose}
        />
      </div>
    </div>
  );
};

export default ChatHeader;
